import { ActivityIndicator, Text, TouchableOpacity, View } from "react-native";
import { FIREBASE_EVENT_NAMES, FIREBASE_EVENT_PARAMS, PAGES_ROUTES, PAGES_TITLE, SORT_OPTIONS } from "utils/Constants";
import Svg, { Path } from "react-native-svg";
import { debounce, getFilterParamsAPIV2, getFilterParamsV2 } from "utils/common";
import { fp, hp, mpAll, spH, spV, wp } from "utils/normalise";
import { getFiltersV2, setSelectedFiltersWithoutUpdatingUrlParams } from "appRedux/reducers/filterSlice";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { router, useGlobalSearchParams, useLocalSearchParams } from "expo-router";
import { useDispatch, useSelector } from "react-redux";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import { Colors } from "colors";
import { ColumnItem } from "components/PLP/ColumnItem";
import FilterChips from "components/filters/FilterChips";
import Filters from "components/filters/Filters";
import ListView from "components/ListView";
import ParentScrollView from "components/ParentScrollView";
import ProductCard from "components/PLP/ProductCard";
import SortingComp from "components/PLP/SortingComp";
import VText from "components/VText";
import { getFilteredProducts } from "appRedux/reducers/homeSlice";
import { trackV2 } from "utils/Segment";
import { Helmet } from "react-helmet";  
import Heading from "components/Heading";
import ProductEmptyScreen from "components/emptyScreen/pdpEmptyScreen";


function formatCategoryString(category) {
    return category
        .split("-") // Split the string by hyphens
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(" "); // Join the words back with a space
}

const Products = ({ isCategory = false }) => {
    const [products, setProducts] = useState([]);
    const [showLoader, setLoader] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalDataCount, setTotalDataCount] = useState(0);
    const [totalPageCount, setTotalPageCount] = useState(0);
    const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0]);
    const [initialloading, setInitialloading] = useState(true);

    const local = useGlobalSearchParams();

    const selectedFilters = useSelector((state) => state.FilterReducer.selectedFilters);
    const parentSelected = useSelector((state) => state.FilterReducer.parentSelected);
    const isLoadingFilters = useSelector((state) => state.FilterReducer.isLoadingFilters);
    const countrySymbol = useSelector((state) => state.StoredutilsSlice.countrySymbol);

    const _scrollRef = useRef();

    const category = local?.category
        ? formatCategoryString(local?.category)
        : local?.title
        ? local?.title
        : local?.searchkey?.toUpperCase() ?? "New Arrivals";

    const gender = local?.gender ?? "female";

    const dispatch = useDispatch();

    useEffect(() => {
        const filter_applied = selectedFilters && Object.values(selectedFilters).some((arr) => arr?.length > 0);
        const eventData = {
            current_section: gender === "female" ? "Women" : "Men",
            plp_title: category,
            filter_value: filter_applied,
            total_products: totalDataCount,
            sort_order: selectedSort?.type,
        };
        if (totalDataCount > 0) {
            trackV2(ANALYTICS_EVENT_NAMES.PLP_VIEWED, eventData);
        }
    }, [totalDataCount]);

    const returnFilterValues = useMemo(() => {
        const filter_value = {};
        const filter_type = [];
        Object.keys(selectedFilters).forEach((key) => {
            if (selectedFilters[key]?.length > 0) {
                switch (key) {
                    case "1":
                        filter_value.category = selectedFilters[key].map((item) => item?.title).join(", ");
                        filter_type.push("category");
                        break;
                    case "2":
                        filter_value.brand = selectedFilters[key].map((item) => item?.title).join(", ");
                        filter_type.push("brand");
                        break;
                    case "3":
                        filter_value.color = selectedFilters[key].map((item) => item?.title).join(", ");
                        filter_type.push("color");
                        break;
                    case "5":
                        filter_value.price = selectedFilters[key].map((item) => item?.title).join(", ");
                        filter_type.push("price");
                        break;
                    case "8":
                        filter_value.size = selectedFilters[key].map((item) => item.title).join(", ");
                        filter_type.push("size");
                        break;
                }
            }
        });
        return { filter_value, filter_type };
    }, [selectedFilters]);

    useEffect(() => {
        const filter_applied = selectedFilters && Object.values(selectedFilters).some((arr) => arr?.length > 0);
        const { filter_value, filter_type } = returnFilterValues;

        if (filter_applied) {
            const eventData = {
                current_section: gender === "female" ? "Women" : "Men",
                plp_title: category,
                filter_type: JSON.stringify(filter_type),
                total_active_filters: Object.values(selectedFilters).filter((arr) => arr?.length > 0).length,
                filter_value: JSON.stringify(filter_value),
                filtered_product_count: totalDataCount,
            };
            trackV2(ANALYTICS_EVENT_NAMES.FILTER_APPLIED, eventData);
        }
    }, [selectedFilters, totalDataCount]);

    useEffect(() => {
        const eventData = {
            new_sort_order: selectedSort?.title,
            plp_title: category,
            total_products: totalDataCount,
        };
        trackV2(ANALYTICS_EVENT_NAMES.SORT_APPLIED, eventData);
    }, [selectedSort?.type]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        let active = true;

        const fetchProducts = debounce(() => {
            setLoader(true);
            const data1 = {};
            const data = getFilterParamsV2({
                ...selectedFilters,
                1: [...(selectedFilters?.[1] || []), ...(parentSelected || [])],
            });
            if (local?.optionId) {
                if ([7, "7", 8, "8"].includes(local?.optionId)) {
                    let temp = [];
                    let obj = {
                        ...selectedFilters,
                        1: [...(selectedFilters?.[1] || []), ...(parentSelected || [])],
                    };
                    let keys = Object.keys(obj);
                    keys.forEach((key) => {
                        if (Array.isArray(obj[key])) {
                            temp.push(obj[key]);
                        }
                    });
                    temp = temp.flat();
                    if (temp?.length == 0) {
                        data1.optionId = local?.optionId;
                    }
                } else {
                    data1.optionId = local?.optionId;
                }
            }
            if (local?.searchkey) {
                data1.key = local?.searchkey;
            }
            data1.page = currentPage;
            const dataTosend = {
                ...data1,
                ...data,
                sortBy: selectedSort.type,
                limit: 30,
                gender: [gender],
            };

            if (!active) {
                return;
            }

            dispatch(getFilteredProducts({ data: dataTosend, abortSignal: signal }))
                .then((res) => {
                    if (!active) {
                        return;
                    }
                    const resData = res?.payload?.data?.data;
                    setLoader(false);
                    if (resData && Array.isArray(resData?.productDetails)) {
                        setTotalDataCount(resData?.total);
                        setTotalPageCount(resData?.totalPage);
                        setProducts([...resData?.productDetails]);
                    }
                })
                .catch((err) => {
                    if (!active) {
                        return;
                    }
                    setLoader(false);
                    console.log("err", err);
                    alert("Error");
                });
        }, 500); // Adjust the debounce delay as needed

        fetchProducts();

        return () => {
            active = false;
            controller.abort(); // Abort the previous API call
        };
    }, [
        JSON.stringify(selectedFilters),
        selectedSort?.type,
        currentPage,
        parentSelected,
        local?.searchkey,
        local?.optionId,
    ]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        const fetchFilters = debounce(() => {
            const data1 = {};
            if (local?.optionId) {
                data1.optionId = local?.optionId;
            }
            if (local?.searchkey) {
                data1.key = local?.searchkey;
            }
            let dataTosend = {
                ...data1,
                ...((!local?.optionId &&
                    getFilterParamsAPIV2({
                        ...selectedFilters,
                        1: [...(selectedFilters?.[1] || []), ...(parentSelected || [])],
                    })) ||
                    {}),
                gender: [gender],
            };

            dispatch(
                getFiltersV2({
                    data: dataTosend,
                    abortSignal: signal, // Pass the abort signal
                })
            );
        }, 300); // Adjust the debounce delay as needed

        fetchFilters();

        return () => {
            controller.abort(); // Abort the previous API call
        };
    }, [selectedFilters, parentSelected, local?.searchkey, local?.optionId, gender]);

    useEffect(() => {
        if (local?.sortBy) {
            try {
                setSelectedSort(SORT_OPTIONS?.find((o) => o?.type == local?.sortBy) || SORT_OPTIONS[0]);
            } catch (err) {}
        }
    }, [local?.sortBy]);

    useEffect(() => {
        if (local?.page) {
            try {
                setCurrentPage(Number(local?.page));
            } catch (err) {}
        }
    }, [local?.page]);

    useEffect(() => {
        if (initialloading) {
            setInitialloading(isLoadingFilters || showLoader);
        }
    }, [initialloading, isLoadingFilters, showLoader]);

    const FilterSection = useCallback(() => {
        return (
            <View
                style={{
                    marginRight: spH(48),
                    width: wp(285),
                }}
            >
                {!initialloading && <Filters />}
                {initialloading && (
                    <ActivityIndicator
                        style={{
                            marginTop: 40,
                        }}
                        size="large"
                        color={Colors.black}
                    />
                )}
            </View>
        );
    }, [initialloading]);

    return (
        <ParentScrollView _scrollRef={_scrollRef}>
            <View
                style={[
                    mpAll(48),
                    {
                        flex: 1,
                        backgroundColor: "white",
                        marginTop: spV(32),
                    },
                ]}
            >
                <Heading
                    type="h1"
                    style={{
                        textAlign: "center",
                        fontSize: fp(28),
                        fontFamily: "LibreBodoni",
                        fontWeight: "350",
                        textTransform: "capitalize",
                        marginLeft: gender == 'male' ?  - 50 : -100
                    }}
                   
                >
                    <span style={{color:'White'}}>{gender} </span> 
                    {category}
                </Heading>

                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: spV(24),
                    }}
                >
                    <View
                        style={{
                            paddingVertical: spV(16),
                            borderBottomWidth: 1,
                            borderColor: "rgba(33, 36, 39, 0.3)",
                            width: wp(285),
                        }}
                    >
                        <VText
                            style={{
                                fontSize: fp(16),
                                color: "rgba(33, 36, 39, 0.6)",
                            }}
                            text={`${totalDataCount} results found`}
                        />
                    </View>
                    <SortingComp selected={selectedSort} setSelected={setSelectedSort} />
                </View>
                <View
                    style={{
                        flexDirection: "row",
                        zIndex: -100,
                    }}
                >
                    <FilterSection />
                    <View
                        style={{
                            flex: 1,
                            zIndex: -10,
                        }}
                    >
                        <FilterChips containerstyle={{ paddingBottom: spV(28) }} />
                        <ListView
                            data={isLoadingFilters || showLoader ? [] : Array.isArray(products) ? products : []}
                            estimatedItemSize={200}
                            numColumns={3}
                            columnWrapperStyle={{ justifyContent: "space-between" }}
                            type={ListView.Type.Flash}
                            showsVerticalScrollIndicator={false}
                            keyExtractor={(item, index) => `${item?.productId?.toString()}_${index}_product`}
                            renderItem={({ item, index }) => (
                                <ColumnItem index={index} numColumns={3}>
                                    <ProductCard
                                        gender={gender}
                                        product={item}
                                        title={category}
                                        current_filters={returnFilterValues || {}}
                                        current_sort_order={selectedSort?.type}
                                        current_page={currentPage}
                                        index={index}
                                    />
                                </ColumnItem>
                            )}
                            ListFooterComponent={() => (
                                <View
                                    style={{
                                        paddingVertical: spV(48),
                                    }}
                                >
                                    <TouchableOpacity
                                        style={{
                                            marginBottom: spV(28),
                                            alignSelf: "flex-end",
                                        }}
                                        activeOpacity={0.8}
                                        onPress={() =>
                                            _scrollRef?.current?.scrollTo({
                                                x: 0,
                                                y: 0,
                                                animated: true,
                                            })
                                        }
                                    >
                                        <Svg
                                            width="60"
                                            height="60"
                                            viewBox="0 0 60 60"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <Path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M30.001 5C16.1935 5 5.00098 16.1925 5.00098 30C5.00098 43.8075 16.1935 55 30.001 55C43.8085 55 55.001 43.8075 55.001 30C55.001 16.1925 43.8085 5 30.001 5ZM30.001 17.5C30.664 17.5001 31.2997 17.7636 31.7685 18.2325L41.7685 28.2325C42.2239 28.704 42.4759 29.3355 42.4702 29.991C42.4645 30.6465 42.2015 31.2735 41.738 31.737C41.2745 32.2006 40.6475 32.4635 39.992 32.4692C39.3365 32.4749 38.705 32.2229 38.2335 31.7675L32.501 26.035V40C32.501 40.663 32.2376 41.2989 31.7687 41.7678C31.2999 42.2366 30.664 42.5 30.001 42.5C29.3379 42.5 28.7021 42.2366 28.2332 41.7678C27.7644 41.2989 27.501 40.663 27.501 40V26.035L21.7685 31.7675C21.297 32.2229 20.6655 32.4749 20.01 32.4692C19.3545 32.4635 18.7275 32.2006 18.2639 31.737C17.8004 31.2735 17.5375 30.6465 17.5318 29.991C17.5261 29.3355 17.7781 28.704 18.2335 28.2325L28.2335 18.2325C28.7022 17.7636 29.338 17.5001 30.001 17.5Z"
                                                fill="#212427"
                                                fillOpacity="0.3"
                                            />
                                        </Svg>
                                    </TouchableOpacity>
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            paddingTop: spV(28),
                                            borderTopWidth: 1,
                                            borderColor: "rgba(33, 36, 39, 0.3)",
                                        }}
                                    >
                                        <TouchableOpacity
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                cursor: currentPage == 1 ? "not-allowed" : "pointer",
                                            }}
                                            activeOpacity={0.8}
                                            onPress={() => {
                                                const eventData = {
                                                    plp_title: category,
                                                    current_page: currentPage,
                                                    previous_page: currentPage - 1,
                                                    pagination_direction: "previous",
                                                };
                                                trackV2(ANALYTICS_EVENT_NAMES.PAGINATION_CLICKED, eventData);
                                                setCurrentPage((prev) => {
                                                    if (prev == 1) {
                                                        return 1;
                                                    } else {
                                                        _scrollRef?.current?.scrollTo({
                                                            x: 0,
                                                            y: 0,
                                                            animated: true,
                                                        });
                                                        router.setParams({ page: prev - 1 });
                                                        return Number(prev) - 1;
                                                    }
                                                });
                                            }}
                                        >
                                            <Svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <Path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M10.0255 14L11 13.0255L5.97452 8L11 2.97452L10.0255 2L4.02548 8L10.0255 14Z"
                                                    fill="#212427"
                                                    fillOpacity={currentPage == 1 ? "0.3" : "1"}
                                                />
                                            </Svg>

                                            <VText
                                                style={{
                                                    fontSize: fp(16),
                                                    color: currentPage == 1 ? "rgba(33, 36, 39, 0.3)" : "#212427",
                                                    paddingLeft: 8,
                                                }}
                                                text={"Previous"}
                                            />
                                        </TouchableOpacity>
                                        <VText
                                            style={{
                                                fontSize: fp(16),
                                                color: "#212427",
                                            }}
                                            text={`Page ${currentPage} of ${totalPageCount}`}
                                        />
                                        <TouchableOpacity
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                cursor: currentPage == totalPageCount ? "not-allowed" : "pointer",
                                            }}
                                            activeOpacity={0.8}
                                            onPress={() => {
                                                const eventData = {
                                                    plp_title: category,
                                                    current_page: currentPage,
                                                    next_page: currentPage + 1,
                                                    pagination_direction: "next",
                                                };
                                                trackV2(ANALYTICS_EVENT_NAMES.PAGINATION_CLICKED, eventData);
                                                setCurrentPage((prev) => {
                                                    if (prev == totalPageCount) {
                                                        return totalPageCount;
                                                    } else {
                                                        _scrollRef?.current?.scrollTo({
                                                            x: 0,
                                                            y: 0,
                                                            animated: true,
                                                        });
                                                        router.setParams({ page: Number(prev) + 1 });
                                                        return Number(prev) + 1;
                                                    }
                                                });
                                            }}
                                        >
                                            <VText
                                                style={{
                                                    fontSize: fp(16),
                                                    color:
                                                        currentPage == totalPageCount
                                                            ? "rgba(33, 36, 39, 0.3)"
                                                            : "#212427",
                                                    paddingRight: 8,
                                                }}
                                                text={"Next"}
                                            />
                                            <Svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <Path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M5.97452 14L5 13.0255L10.0255 8L5 2.97452L5.97452 2L11.9745 8L5.97452 14Z"
                                                    fill={
                                                        currentPage == totalPageCount
                                                            ? "rgba(33, 36, 39, 0.3)"
                                                            : "#212427"
                                                    }
                                                />
                                            </Svg>
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            )}
                            ListEmptyComponent={() => (
                                <View
                                    style={{
                                        alignSelf: "center",
                                        paddingTop: hp(50),
                                        minHeight: hp(500),
                                    }}
                                >
                                    {(isLoadingFilters || showLoader) && (
                                        <ActivityIndicator size="large" color={Colors.black} />
                                    )}
                                    {!isLoadingFilters && !showLoader && (
                                        <VText
                                            style={{
                                                color: Colors.black60,
                                                fontSize: fp(15),
                                            }}
                                        >
                                            <ProductEmptyScreen title={category} gender={gender} />
                                        </VText>
                                    )}
                                </View>
                            )}
                        />
                    </View>
                </View>
            </View>
        </ParentScrollView>
    );
};

export default memo(Products);
